import { useState, useEffect } from "react";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, PawPrint } from "lucide-react";
import { useMotionValue } from "framer-motion";
import PageMeta from "../components/PageMeta";


function Cursor() {
  const mx = useMotionValue(-200);
  const my = useMotionValue(-200);
  useEffect(() => {
    const move = (e: MouseEvent) => { mx.set(e.clientX); my.set(e.clientY); };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);
  return (
    <motion.div className="fixed pointer-events-none z-[9999]" style={{ x: mx, y: my, translateX: "-50%", translateY: "-50%" }}>
      <div className="w-2 h-2 rounded-full bg-primary" />
    </motion.div>
  );
}

const TIER_OPTIONS = [
  {
    id: "Steadfast",
    label: "The Steadfast",
    price: "S$680/yr",
    monthly: "S$57/mo",
    desc: "Annual wellness, core vaccinations, genomics baseline",
  },
  {
    id: "Devoted",
    label: "The Devoted",
    price: "S$1,180/yr",
    monthly: "S$98/mo",
    desc: "Bi-annual exams, bloodwork, advanced genomics, urgent care",
    popular: true,
  },
  {
    id: "Besotted",
    label: "The Besotted",
    price: "S$2,980/yr",
    monthly: "S$248/mo",
    desc: "Full workup, 24/7 care line, dental, specialist access",
  },
];

const REFERRAL_OPTIONS = [
  "Instagram / Social media",
  "A friend or family member",
  "Google search",
  "Editorial / Press",
  "A Furbaby event",
  "Other",
];

const SPECIES_OPTIONS = ["Dog", "Cat", "Rabbit", "Bird", "Hamster", "Guinea pig", "Other"];

type Step = 1 | 2 | 3 | "success";

export default function Book() {
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    petName: "",
    species: "",
    breed: "",
    tierInterest: "Devoted",
    referralSource: "",
  });

  useEffect(() => {
    fetch("/api/waitlist/count")
      .then(r => r.json())
      .then(d => setCount(d.count))
      .catch(() => {});
  }, []);

  const patch = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const canNext1 = form.firstName.trim() && form.email.trim().includes("@");
  const canNext2 = form.petName.trim() && form.species;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          petName: form.petName,
          species: form.species,
          breed: form.breed,
          tierInterest: form.tierInterest,
          referralSource: form.referralSource,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        if (data.error?.includes("duplicate") || data.error?.includes("already")) {
          setStep("success");
          return;
        }
        throw new Error(data.error ?? "Something went wrong");
      }
      const data = await res.json();
      setCount(data.count ?? count);
      setStep("success");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const inputCls = "w-full bg-transparent border-b border-foreground/20 py-3 font-sans text-base focus:outline-none focus:border-primary transition-colors duration-300 placeholder:opacity-25 cursor-none";
  const labelCls = "font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-1 block";

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden selection:bg-primary/40 cursor-none">
      <PageMeta
        title="Founding Member Waitlist — Furbaby Vet Care"
        description="Join the founding member waitlist for Furbaby, opening in Holland Village, Singapore. Founding members earn accelerated loyalty, priority booking, and exclusive perks."
        canonical="https://furbaby.vet/main/book"
        ogTitle="Join the Furbaby Founding Member Waitlist"
        ogDescription="Be among the first 2,000 founding members at Furbaby Holland Village. Earn 10%/yr loyalty — reaching 90% off by Year 9."
      />
      <Cursor />

      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-6 flex justify-between items-center">
        <Link href="/" className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 cursor-none">
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform duration-500" />
          Furbaby
        </Link>
        <Link href="/membership" className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 cursor-none">
          Membership
          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-500" />
        </Link>
      </nav>

      <section className="relative min-h-[45vh] flex items-end px-4 md:px-20 pb-16 pt-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-background to-secondary/10 pointer-events-none" />
        <motion.img
          src={`${import.meta.env.BASE_URL}wall-3.png`}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover object-right opacity-0"
          animate={{ opacity: 0.12 }}
          transition={{ duration: 2, delay: 0.2 }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/70 to-transparent pointer-events-none" />
        <div className="relative z-10 max-w-3xl">
          <motion.p
            className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-5"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Founding Member Waitlist
          </motion.p>
          <div className="overflow-hidden mb-4">
            <motion.h1
              className="font-serif text-[11vw] md:text-[5.5vw] leading-[0.88] tracking-tighter"
              initial={{ y: "110%" }} animate={{ y: "0%" }}
              transition={{ duration: 1.3, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              Furbaby opens in<br /><span className="italic text-primary">Holland Village.</span>
            </motion.h1>
          </div>
          <motion.p
            className="font-sans text-base md:text-lg opacity-60 max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 0.6, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
          >
            Founding membership is limited to 2,000. Join the waitlist now to lock in the accelerated loyalty schedule — 10% per year, reaching 90% off all hospital bills by Year 9.
          </motion.p>
          {count !== null && (
            <motion.p
              className="font-grotesk text-xs uppercase tracking-widest text-primary mt-4 opacity-80"
              initial={{ opacity: 0 }} animate={{ opacity: 0.8 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              {count.toLocaleString()} founding members already on the list
            </motion.p>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 border-t border-border/30">
        <motion.div
          className="lg:col-span-7 px-4 md:px-20 py-16 border-r border-border/30"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          {step !== "success" && (
            <div className="flex gap-1 mb-10">
              {([1, 2, 3] as const).map(s => (
                <div
                  key={s}
                  className={`flex-1 h-0.5 rounded-full transition-colors duration-500 ${step >= s ? "bg-primary" : "bg-border"}`}
                />
              ))}
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.form
                key="step1"
                onSubmit={e => { e.preventDefault(); if (canNext1) setStep(2); }}
                initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              >
                <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-40 mb-8">01 — About you</p>
                <div className="flex flex-col gap-6 max-w-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>First name *</label>
                      <input type="text" value={form.firstName} onChange={patch("firstName")} placeholder="Your name" className={inputCls} autoFocus />
                    </div>
                    <div>
                      <label className={labelCls}>Last name</label>
                      <input type="text" value={form.lastName} onChange={patch("lastName")} placeholder="Optional" className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Email address *</label>
                    <input type="email" value={form.email} onChange={patch("email")} placeholder="you@example.com" className={inputCls} />
                  </div>
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={!canNext1}
                      className="group inline-flex items-center gap-3 font-grotesk text-sm uppercase tracking-widest border-b border-foreground pb-2 hover:opacity-60 transition-opacity duration-300 disabled:opacity-25 disabled:cursor-not-allowed cursor-none"
                    >
                      Continue
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
                    </button>
                  </div>
                </div>
              </motion.form>
            )}

            {step === 2 && (
              <motion.form
                key="step2"
                onSubmit={e => { e.preventDefault(); if (canNext2) setStep(3); }}
                initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              >
                <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-40 mb-8">02 — Your furchild</p>
                <div className="flex flex-col gap-6 max-w-lg">
                  <div>
                    <label className={labelCls}>Pet name *</label>
                    <input type="text" value={form.petName} onChange={patch("petName")} placeholder="e.g. Biscuit" className={inputCls} autoFocus />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Species *</label>
                      <select value={form.species} onChange={patch("species")} className={inputCls}>
                        <option value="">Select…</option>
                        {SPECIES_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Breed</label>
                      <input type="text" value={form.breed} onChange={patch("breed")} placeholder="e.g. Shiba Inu" className={inputCls} />
                    </div>
                  </div>
                  <div className="flex items-center gap-6 pt-4">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="font-grotesk text-xs uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity cursor-none"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={!canNext2}
                      className="group inline-flex items-center gap-3 font-grotesk text-sm uppercase tracking-widest border-b border-foreground pb-2 hover:opacity-60 transition-opacity duration-300 disabled:opacity-25 cursor-none"
                    >
                      Continue
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
                    </button>
                  </div>
                </div>
              </motion.form>
            )}

            {step === 3 && (
              <motion.form
                key="step3"
                onSubmit={submit}
                initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              >
                <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-40 mb-8">03 — Membership interest</p>
                <div className="flex flex-col gap-3 mb-8 max-w-lg">
                  {TIER_OPTIONS.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, tierInterest: t.id }))}
                      className={`text-left p-5 border rounded-2xl transition-all duration-300 cursor-none ${
                        form.tierInterest === t.id ? "border-primary bg-primary/8" : "border-border/40 hover:border-primary/40"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="font-serif text-xl">{t.label}</span>
                        <div className="flex items-center gap-2">
                          {t.popular && (
                            <span className="font-grotesk text-[8px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-primary/15 text-primary">Popular</span>
                          )}
                          <span className="font-grotesk text-xs opacity-55">{t.price}</span>
                        </div>
                      </div>
                      <p className="font-sans text-xs opacity-50">{t.desc}</p>
                    </button>
                  ))}
                </div>
                <div className="mb-8 max-w-lg">
                  <label className={labelCls}>How did you hear about us?</label>
                  <select value={form.referralSource} onChange={patch("referralSource")} className={inputCls}>
                    <option value="">Select…</option>
                    {REFERRAL_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                {error && <p className="font-sans text-sm text-destructive mb-4">{error}</p>}
                <div className="flex items-center gap-6">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="font-grotesk text-xs uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity cursor-none"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="group inline-flex items-center gap-3 font-grotesk text-sm uppercase tracking-widest border-b border-foreground pb-2 hover:opacity-60 transition-opacity duration-300 disabled:opacity-50 cursor-none"
                  >
                    {loading ? "Joining…" : "Join the Founding Waitlist"}
                    {!loading && <Check className="w-4 h-4" />}
                  </button>
                </div>
              </motion.form>
            )}

            {step === "success" && (
              <motion.div
                key="success"
                className="flex flex-col items-start"
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              >
                <motion.div
                  className="w-14 h-14 bg-primary rounded-full flex items-center justify-center mb-8"
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.1, type: "spring", stiffness: 200 }}
                >
                  <PawPrint className="w-7 h-7 text-primary-foreground" strokeWidth={1.5} />
                </motion.div>
                <h2 className="font-serif text-5xl md:text-7xl tracking-tighter mb-4">You're on the list.</h2>
                {count !== null && (
                  <p className="font-grotesk text-xs uppercase tracking-widest text-primary mb-6">
                    Founding member #{count.toLocaleString()} of 2,000
                  </p>
                )}
                <p className="font-sans text-lg opacity-60 max-w-md mb-8 leading-relaxed">
                  A confirmation is on its way to <strong className="opacity-90">{form.email}</strong>.
                  We'll be in touch when founding membership spots formally open.
                </p>
                <div className="p-6 rounded-2xl bg-primary/8 border border-primary/20 mb-10 max-w-md">
                  <p className="font-grotesk text-[10px] uppercase tracking-widest text-primary mb-2">Your founding member loyalty schedule</p>
                  <p className="font-sans text-sm opacity-65 leading-relaxed">
                    10% per year · reaches 90% by Year 9 · locked to your join date, for life.
                    Standard members (after the first 2,000) reach the same ceiling at Year 12.
                  </p>
                </div>
                <Link
                  href="/"
                  className="group inline-flex items-center gap-3 font-grotesk text-sm uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 border-b border-foreground pb-2 cursor-none"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-500" />
                  Back to home
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.div
          className="lg:col-span-5 px-4 md:px-12 py-16 bg-muted/15"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-40 mb-8">Why join the founding waitlist</p>
          <div className="flex flex-col gap-6">
            {[
              {
                title: "Faster loyalty growth",
                body: "Founding members earn 10%/yr loyalty discount — reaching 90% off all hospital bills by Year 9. Standard members take until Year 12 to reach the same ceiling.",
              },
              {
                title: "Priority access",
                body: "Founding members get first access to appointment slots when the clinic opens. No competing with a 2,000-person list — you built the list.",
              },
              {
                title: "Locked-in pricing",
                body: "Your membership price is locked at today's founding rate. Annual pricing may increase as the clinic grows and demand rises.",
              },
              {
                title: "Genomics baseline, day one",
                body: "Every tier includes FurGenomics — your pet's DNA shapes their care from the very first visit. No extra cost, no separate booking.",
              },
              {
                title: "No payment now",
                body: "Joining the waitlist is free. You'll complete payment when the clinic opens and your membership formally activates — no charge before then.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                className="pb-6 border-b border-border/30 last:border-0"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.5 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              >
                <h4 className="font-serif text-xl mb-2">{item.title}</h4>
                <p className="font-sans text-sm opacity-55 leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      <footer className="bg-primary py-16 px-4 md:px-20 flex flex-col md:flex-row justify-between items-center gap-8">
        <div>
          <span className="font-serif text-3xl font-bold italic tracking-tighter">Furbaby</span>
          <p className="font-grotesk text-xs uppercase tracking-widest opacity-50 mt-2">© 2025 Furbaby Vet Care · Singapore</p>
        </div>
        <div className="flex gap-8 font-grotesk text-xs uppercase tracking-widest">
          <Link href="/membership" className="hover:opacity-60 transition-opacity cursor-none">Membership</Link>
          <Link href="/furgenomics" className="hover:opacity-60 transition-opacity cursor-none">FurGenomics</Link>
          <Link href="/journal" className="hover:opacity-60 transition-opacity cursor-none">Journal</Link>
        </div>
      </footer>
    </div>
  );
}
